import { useParams, Link } from "react-router-dom";
import { useT } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContentContext";

/**
 * Help & Info pages — Contact / FAQ / Shipping / Returns / Size Guide.
 *
 * All copy is driven by translation keys under the `help.` prefix and is
 * therefore editable from the Admin → Content tab (each language separately).
 * Body paragraphs are numbered `help.<topic>.body.1..N` and rendered until
 * the first missing key so the admin can add / remove lines freely.
 */

const TOPICS = ["contact", "faq", "shipping", "returns"];

// Longest supported body length per topic (upper bound of paragraphs the
// page will render). Anything beyond this must extend the constant + add
// keys to `translations.js`. Kept generous on purpose.
const MAX_BODY_LINES = 8;

const Help = () => {
  const { topic } = useParams();
  const { t } = useT();
  const { settings } = useSiteContent();
  const threshold = settings?.free_shipping_threshold ?? 150;
  const active = TOPICS.includes(topic) ? topic : "contact";

  // Walk body.1..N — stop at the first line that has no override AND no
  // compiled default (t() returns the key itself when nothing is found).
  const paragraphs = [];
  for (let i = 1; i <= MAX_BODY_LINES; i += 1) {
    const key = `help.${active}.body.${i}`;
    const val = t(key, { threshold });
    if (!val || val === key) break;
    paragraphs.push({ key, val });
  }

  const titleKey = `help.${active}.title`;
  const eyebrowKey = "help.eyebrow";
  const backKey = "help.back";

  return (
    <div className="pt-24 md:pt-32 pb-24 px-6 md:px-12 max-w-3xl mx-auto">
      <div className="font-typewriter text-[10px] tracking-[0.32em] uppercase text-[var(--gl-orange)]">
        {t(eyebrowKey)}
      </div>
      <h1
        data-testid={`help-title-${active}`}
        className="font-serif-display text-4xl md:text-6xl mt-3 tracking-tight text-[#2C2C2C]"
      >
        {t(titleKey)}
      </h1>

      <div
        data-testid={`help-body-${active}`}
        className="mt-8 space-y-4 text-[#2C2C2C] text-base md:text-lg leading-relaxed whitespace-pre-wrap"
      >
        {paragraphs.map(({ key, val }) => (
          <p key={key} data-testid={key}>{val}</p>
        ))}
      </div>

      <div className="mt-12">
        <Link to="/" className="btn-orange">{t(backKey)}</Link>
      </div>
    </div>
  );
};

export default Help;
